import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from "react-native";
import { useState, useRef, useEffect } from "react";
import { Feather, Ionicons, FontAwesome5, Entypo } from "@expo/vector-icons";
import Colors from "../../../Resources/Color";
import { useDevice, useScreenDimensions } from "../../_layout";
import ContinueButton from "./ContinueButton";
import PlatformPreview from "./PlatformPreview";
import SelectionCard from "./SelectionCard";
import NumberCard from "./NumberCard";
import Tooltip from "../../Components/Tooltip";
import { useTranslation } from "react-i18next";
import NoTranslateText from "../../Components/NoTranslateText";

/**
 * Step 0: Name Input
 * Ask for the coach's name
 */
export const NameStep = ({ value, onChange, onContinue }) => {
  const { screenSize } = useScreenDimensions();
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const trimmedValue = value.trim();
  const isDisabled = trimmedValue.length < 2;

  useEffect(() => {
    // Auto-focus the input when component mounts
    if (inputRef.current && screenSize !== "small") {
      inputRef.current.focus();
    }
  }, []);

  const handleContinue = () => {
    if (!isDisabled && onContinue) {
      // Trim the value before continuing
      onChange(trimmedValue);
      onContinue();
    }
  };

  return (
    <View
      style={[
        styles.stepContainer,
        { paddingHorizontal: screenSize === "small" ? 12 : 24 },
      ]}
    >
      <Text style={styles.title}>{t("onboardingNameTitle-string")}</Text>
      <Text style={styles.subtitle}>{t("onboardingNameSubtitle-string")}</Text>

      <TextInput
        ref={inputRef}
        style={[
          styles.input,
          {
            fontSize: screenSize === "small" ? 16 : 14,
            borderWidth: 1,
            borderColor: isFocused ? Colors.bubbleColor : Colors.borderColor,
          },
        ]}
        placeholder={t("onboardingNamePlaceholder-string")}
        value={value}
        onChangeText={onChange}
        onSubmitEditing={handleContinue}
        returnKeyType={"done"}
        placeholderTextColor={Colors.grayColor}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />

      <ContinueButton
        onPress={handleContinue}
        disabled={isDisabled}
        text={t("continue-string")}
      />
    </View>
  );
};

/**
 * Step 1: Number of Athletes
 * Ask how many athletes the coach has
 */
export const AthletesCountStep = ({ selectedRange, onSelect, onContinue }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const { t } = useTranslation();

  const ranges = [
    {
      id: "1-5",
      label: t("onboardingAthletesCount1-5-string"),
      display: "1 - 5",
    },
    {
      id: "6-15",
      label: t("onboardingAthletesCount6-15-string"),
      display: "6 - 15",
    },
    {
      id: "16-30",
      label: t("onboardingAthletesCount16-30-string"),
      display: "16 - 30",
    },
    {
      id: "31-50",
      label: t("onboardingAthletesCount31-50-string"),
      display: "31 - 50",
    },
    {
      id: "50+",
      label: t("onboardingAthletesCount50plus-string"),
      display: "+ 50",
    },
  ];

  const handleSelect = (range) => {
    onSelect(range.id);
    setIsSelecting(true);
    // Auto-advance to next step after selection
    if (onContinue) {
      setTimeout(() => {
        onContinue();
      }, 600);
    }
  };

  return (
    <View
      style={[
        styles.stepContainer,
        { paddingHorizontal: screenSize === "small" ? 12 : 24 },
      ]}
    >
      <Text style={styles.title}>
        {t("onboardingAthletesCountTitle-string")}
      </Text>

      <View
        style={[
          styles.cardGridContainer,
          { marginTop: 24, gap: screenSize === "small" ? 8 : 12 },
        ]}
      >
        {ranges.map((range, index) => {
          const isSelected = selectedRange === range.id;
          const shouldFadeOut = isSelecting && !isSelected;

          return (
            <NumberCard
              key={range.id}
              number={range.display}
              isSelected={isSelected}
              isHovered={hoveredIndex === index}
              shouldFadeOut={shouldFadeOut}
              onPress={() => handleSelect(range)}
              onMouseEnter={() => !isMobile && setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              animationDelay={index * 50}
            />
          );
        })}
      </View>
    </View>
  );
};

/**
 * Step 2: Type of Athletes
 * Ask what type of athletes the coach works with
 */
export const AthleteTypeStep = ({ selectedTypes, onToggle, onContinue }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const { t } = useTranslation();

  const types = [
    {
      id: "powerlifters",
      label: t("onboardingAthleteTypePowerlifters-string"),
      icon: (
        <Ionicons name="barbell-sharp" size={20} color={Colors.blackColor} />
      ),
    },
    {
      id: "bodybuilders",
      label: t("onboardingAthleteTypeBodybuilders-string"),
      icon: (
        <Ionicons
          name="barbell-sharp"
          size={18}
          color={Colors.blackColor}
          style={{ transform: [{ rotate: "-45deg" }] }}
        />
      ),
    },
    {
      id: "other",
      label: t("onboardingAthleteTypeOther-string"),
      icon: (
        <Entypo
          name="dots-three-horizontal"
          size={18}
          color={Colors.blackColor}
        />
      ),
    },
  ];
  const isDisabled = selectedTypes.length === 0;

  return (
    <View
      style={[
        styles.stepContainer,
        { paddingHorizontal: screenSize === "small" ? 12 : 24 },
      ]}
    >
      <Text style={styles.title}>{t("onboardingAthleteTypeTitle-string")}</Text>
      <Text style={styles.subtitle}>
        {t("onboardingAthleteTypeSubtitle-string")}
      </Text>

      <View
        style={[
          styles.cardGridContainer,
          { gap: screenSize === "small" ? 8 : 12 },
        ]}
      >
        {types.map((type, index) => {
          const isSelected = selectedTypes.includes(type.id);

          return (
            <SelectionCard
              key={type.id}
              icon={type.icon}
              text={type.label}
              isSelected={isSelected}
              isHovered={hoveredIndex === index}
              onPress={() => onToggle(type.id)}
              onMouseEnter={() => !isMobile && setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              animationDelay={index * 50}
            />
          );
        })}
      </View>

      <ContinueButton
        onPress={onContinue}
        disabled={isDisabled}
        text={t("continue-string")}
      />
    </View>
  );
};

/**
 * Step 3: Features Selection
 * Ask what features the coach plans to use
 */
export const FeaturesStep = ({
  selectedFeatures,
  athleteTypes,
  logoUri,
  onToggle,
  onContinue,
}) => {
  const { screenSize } = useScreenDimensions();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const { isMobile } = useDevice();
  const { t } = useTranslation();

  const allFeatures = [
    {
      id: "programs",
      label: t("onboardingFeaturePrograms-string"),
      icon: (
        <Ionicons
          name="clipboard-outline"
          size={20}
          color={Colors.blackColor}
        />
      ),
    },
    {
      id: "videos",
      label: t("onboardingFeatureVideos-string"),
      icon: (
        <Ionicons name="chatbox-outline" size={20} color={Colors.blackColor} />
      ),
    },
    {
      id: "competitions",
      label: t("onboardingFeatureCompetitions-string"),
      icon: (
        <Ionicons name="podium-outline" size={20} color={Colors.blackColor} />
      ),
    },
    {
      id: "billing",
      label: t("onboardingFeatureBilling-string"),
      icon: (
        <Ionicons name="card-outline" size={20} color={Colors.blackColor} />
      ),
    },
    {
      id: "forms",
      label: t("onboardingFeatureForms-string"),
      icon: (
        <Ionicons name="reader-outline" size={20} color={Colors.blackColor} />
      ),
    },
  ];

  // Only show competitions feature if Powerlifters is selected
  const features = allFeatures.filter(
    (feature) =>
      feature.id !== "competitions" ||
      (athleteTypes && athleteTypes.includes("powerlifters")),
  );

  const isDisabled = selectedFeatures.length === 0;

  return (
    <View
      style={[
        styles.stepContainer,
        styles.featuresStep,
        {
          gap: screenSize === "large" ? 4 : 4,
          paddingHorizontal: screenSize === "small" ? 12 : 24,
        },
      ]}
    >
      <View
        style={[
          styles.featuresLeftColumn,
          { minWidth: screenSize != "small" ? 320 : undefined },
        ]}
      >
        <NoTranslateText style={styles.title}>
          {t("onboardingFeaturesTitle-string")}
        </NoTranslateText>
        <Text style={styles.subtitle}>
          {t("onboardingFeaturesSubtitle-string")}
        </Text>

        <View
          style={[
            styles.cardGridContainer,
            { gap: screenSize === "small" ? 8 : 12 },
          ]}
        >
          {features.map((feature, index) => {
            const isSelected = selectedFeatures.includes(feature.id);

            return (
              <SelectionCard
                key={feature.id}
                icon={feature.icon}
                text={feature.label}
                isSelected={isSelected}
                isHovered={hoveredIndex === index}
                onPress={() => onToggle(feature.id)}
                onMouseEnter={() => !isMobile && setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                animationDelay={200 + index * 80}
              />
            );
          })}
        </View>

        <ContinueButton
          onPress={onContinue}
          disabled={isDisabled}
          text={t("continue-string")}
        />
      </View>

      {screenSize != "small" && (
        <View style={styles.featuresRightColumn}>
          <PlatformPreview
            selectedFeatures={selectedFeatures}
            athleteTypes={athleteTypes}
          />
        </View>
      )}
    </View>
  );
};

/**
 * Step 4: Logo Upload
 * Ask coach to upload their logo
 */
export const LogoStep = ({
  logoUri,
  onUpload,
  selectedFeatures = [],
  athleteTypes = [],
  onContinue,
}) => {
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const { t } = useTranslation();
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploadButtonHovered, setIsUploadButtonHovered] = useState(false);
  const [isDeleteButtonHovered, setIsDeleteButtonHovered] = useState(false);
  const [isSkipHovered, setIsSkipHovered] = useState(false);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
  const ALLOWED_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
  ];

  const processFile = async (file) => {
    if (!file) return;

    setUploadError(null);
    setIsUploading(true);

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError(t("onboardingLogoErrorInvalidType-string"));
      setIsUploading(false);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(t("onboardingLogoErrorFileSize-string"));
      setIsUploading(false);
      return;
    }

    try {
      // Create a data URL for preview
      const reader = new FileReader();
      reader.onload = (e) => {
        if (onUpload) {
          onUpload(e.target.result);
        }
        setIsUploading(false);
      };
      reader.onerror = () => {
        setUploadError(t("onboardingLogoErrorReadFile-string"));
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setUploadError(t("onboardingLogoErrorProcessing-string"));
      setIsUploading(false);
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    await processFile(file);
    // Clear the input so the same file can be selected again
    event.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types && e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types && e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragOver to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processFile(files[0]);
    }
  };

  const handleUpload = () => {
    const fileInput = document.getElementById("logoFileInput");
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleDeleteLogo = () => {
    setUploadError(null);
    if (onUpload) {
      onUpload(null); // Clear the logo
    }
  };

  const isDisabled = !logoUri && !isMobile;

  // Responsive logo size based on screen size
  const logoSize = screenSize === "small" ? 100 : 140;
  const containerGap = screenSize === "small" ? 16 : 16;

  return (
    <View
      style={[
        styles.stepContainer,
        styles.featuresStep,
        {
          gap: screenSize === "large" ? 4 : 4,
          paddingHorizontal: screenSize === "small" ? 12 : 24,
        },
      ]}
    >
      {screenSize != "small" && (
        <View style={[styles.featuresRightColumn, { alignItems: "flex-end" }]}>
          <PlatformPreview
            selectedFeatures={selectedFeatures}
            logoUri={logoUri}
            athleteTypes={athleteTypes}
            showLogo={true}
          />
        </View>
      )}

      <View
        style={[
          styles.featuresLeftColumn,
          { minWidth: screenSize != "small" ? 320 : undefined },
        ]}
      >
        <Text style={styles.title}>{t("onboardingLogoTitle-string")}</Text>
        <Text style={styles.subtitle}>
          {t("onboardingLogoSubtitle-string")}
        </Text>

        <div
          style={{
            ...styles.logoUploadContainer,
            ...(isDragOver && styles.logoUploadContainerDragOver),
            gap: containerGap,
            paddingTop: screenSize === "small" ? 16 : 24,
            paddingBottom: screenSize === "small" ? 16 : 24,
            paddingLeft: screenSize === "large" ? 24 : 12,
            paddingRight: screenSize === "large" ? 24 : 12,
            boxSizing: "border-box",
            minWidth: 0, // Prevents overflow
            flexShrink: 1, // Allows shrinking when needed
          }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <View style={[styles.logoCircleContainer, { flexShrink: 1 }]}>
            <View
              style={[
                styles.logoCircle,
                {
                  width: logoSize,
                  height: logoSize,
                  borderRadius: logoSize / 2,

                  backgroundColor: logoUri
                    ? "transparent"
                    : Colors.lightGrayColor,
                },
              ]}
            >
              {logoUri ? (
                <img
                  src={logoUri}
                  alt="Logo preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 16,
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Text
                  style={[
                    styles.logoPlaceholder,
                    { fontSize: screenSize === "small" ? 18 : 24 },
                  ]}
                >
                  {t("onboardingLogoPlaceholder-string")}
                </Text>
              )}
            </View>
            {logoUri && (
              <TouchableOpacity
                style={[
                  styles.deleteButton,
                  isDeleteButtonHovered && styles.deleteButtonHovered,
                ]}
                onPress={handleDeleteLogo}
                onMouseEnter={() => !isMobile && setIsDeleteButtonHovered(true)}
                onMouseLeave={() => setIsDeleteButtonHovered(false)}
              >
                <Feather
                  name="x"
                  size={16}
                  color={
                    isDeleteButtonHovered ? Colors.whiteColor : Colors.grayColor
                  }
                />
              </TouchableOpacity>
            )}
          </View>

          <View style={[styles.uploadButtonContainer, { flexShrink: 1 }]}>
            <TouchableOpacity
              style={[
                styles.uploadButton,
                isUploading && styles.uploadButtonDisabled,
                isUploadButtonHovered &&
                  !isUploading &&
                  styles.uploadButtonHovered,
                { paddingHorizontal: screenSize === "small" ? 16 : 20 },
              ]}
              onPress={handleUpload}
              disabled={isUploading}
              onMouseEnter={() => !isMobile && setIsUploadButtonHovered(true)}
              onMouseLeave={() => setIsUploadButtonHovered(false)}
            >
              <Feather
                name={isUploading ? "loader" : "upload"}
                size={screenSize === "small" ? 14 : 16}
                color={isUploading ? Colors.grayColor : Colors.bubbleColor}
              />
              <Text
                style={[
                  styles.uploadButtonText,
                  isUploading && styles.uploadButtonTextDisabled,
                  { fontSize: screenSize === "small" ? 12 : 14, flexShrink: 1 },
                ]}
              >
                {isUploading
                  ? t("onboardingLogoUploading-string")
                  : logoUri
                    ? t("onboardingLogoChangeFile-string")
                    : t("onboardingLogoChooseFile-string")}
              </Text>
            </TouchableOpacity>
            {!logoUri && (
              <Text
                style={[
                  styles.dragDropText,
                  { fontSize: screenSize === "small" ? 10 : 12 },
                ]}
              >
                {t("onboardingLogoDragDrop-string")}
              </Text>
            )}
          </View>
        </div>

        {/* Hidden file input */}
        <input
          type="file"
          id="logoFileInput"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageChange}
        />

        {uploadError && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{uploadError}</Text>
          </View>
        )}

        <ContinueButton
          onPress={onContinue}
          disabled={isDisabled}
          text={t("continue-string")}
        />

        {isDisabled && (
          <TouchableOpacity
            onPress={onContinue}
            style={styles.skipButton}
            onMouseEnter={() => !isMobile && setIsSkipHovered(true)}
            onMouseLeave={() => setIsSkipHovered(false)}
          >
            <Text
              style={[styles.skipText, isSkipHovered && styles.skipTextHovered]}
            >
              {t("onboardingLogoSkip-string")}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

/**
 * Step 5: Preferences
 * Ask coach to set their preferences
 */
export const PreferencesStep = ({
  preferences,
  onPreferenceChange,
  onFinish,
  isSaving = false,
}) => {
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const { t, i18n } = useTranslation();
  const preferenceOptions = [
    {
      id: "exercise_unit",
      label: t("onboardingPreferenceMeasurementUnit-string"),
      options: [
        { value: false, label: t("onboardingPreferenceKg-string") },
        { value: true, label: t("onboardingPreferenceLbs-string") },
      ],
    },
    {
      id: "rir_or_rpe",
      label: t("onboardingPreferenceIntensityRating-string"),
      options: [
        { value: false, label: t("onboardingPreferenceRPE-string") },
        { value: true, label: t("onboardingPreferenceRIR-string") },
      ],
      tooltip: "onboardingPreferenceIntensityRatingTooltip-string",
    },
    {
      id: "show_athlete_exercise_stats",
      label: t("onboardingPreferenceShowExerciseStats-string"),
      options: [
        { value: true, label: t("onboardingPreferenceShow-string") },
        { value: false, label: t("onboardingPreferenceHide-string") },
      ],
      tooltip: "onboardingPreferenceShowExerciseStatsTooltip-string",
    },
    {
      id: "show_set_priority",
      label: t("onboardingPreferenceShowPriority-string"),
      options: [
        { value: true, label: t("onboardingPreferenceShow-string") },
        { value: false, label: t("onboardingPreferenceHide-string") },
      ],
      tooltip: "onboardingPreferenceShowPriorityTooltip-string",
    },
  ];

  return (
    <View
      style={[
        styles.stepContainer,
        { paddingHorizontal: screenSize === "small" ? 12 : 24 },
      ]}
    >
      <Text style={styles.title}>{t("onboardingPreferencesTitle-string")}</Text>
      <Text style={styles.subtitle}>
        {t("onboardingPreferencesSubtitle-string")}
      </Text>

      <View
        style={[
          styles.preferencesContainer,
          { gap: screenSize === "small" ? 8 : 12 },
        ]}
      >
        {preferenceOptions.map((pref) => (
          <View key={pref.id} style={styles.preferenceRow}>
            <View style={styles.preferenceLabelContainer}>
              <Text style={styles.preferenceLabel}>{pref.label}</Text>
              {pref.tooltip && (
                <Tooltip content={pref.tooltip} placement="top" maxWidth={300}>
                  <View style={styles.tooltipIconContainer}>
                    <Ionicons
                      name="information-circle-outline"
                      size={18}
                      color={Colors.grayColor}
                    />
                  </View>
                </Tooltip>
              )}
            </View>
            <View style={styles.preferenceOptions}>
              {pref.options.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={[
                    styles.preferenceOption,
                    isMobile && styles.preferenceOptionMobile,
                    preferences[pref.id] === option.value &&
                      styles.preferenceOptionSelected,
                  ]}
                  onPress={() => onPreferenceChange(pref.id, option.value)}
                >
                  <Text
                    style={[
                      styles.preferenceOptionText,
                      preferences[pref.id] === option.value &&
                        styles.preferenceOptionTextSelected,
                    ]}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        ))}
      </View>

      <ContinueButton
        onPress={() => onFinish(i18n.language, screenSize)}
        disabled={isSaving}
        isLoading={isSaving}
        text={t("onboardingBuildApp-string")}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  stepContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 12,
    maxWidth: "100%",
  },
  title: {
    fontSize: 28,
    fontWeight: "600",
    textAlign: "center",
    marginBottom: 16,
    color: Colors.blackColor,
  },
  subtitle: {
    fontSize: 14,
    textAlign: "center",
    color: Colors.blackColor,
    marginBottom: 40,
  },
  input: {
    maxWidth: 360,
    width: "100%",
    height: 46,
    borderRadius: 14,
    backgroundColor: Colors.whiteColor,
    paddingHorizontal: 16,
    marginBottom: 24,
    outlineStyle: "none",
  },
  button: {
    width: "100%",
    maxWidth: 440,
    height: 52,
    borderRadius: 12,
    backgroundColor: Colors.bubbleColor,
    justifyContent: "center",
    alignItems: "center",
  },
  optionButtonSelected: {
    backgroundColor: `${Colors.bubbleColor}10`,
    borderColor: Colors.bubbleColor,
  },
  optionText: {
    fontSize: 14,
    color: Colors.blackColor,
    textAlign: "center",
    fontWeight: "500",
  },
  optionTextSelected: {
    // fontWeight: "600",
  },
  featuresStep: {
    flexDirection: "row",
    alignSelf: "center",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    maxWidth: 1280,
  },
  featuresLeftColumn: {
    flex: 1,
    width: "100%",
    maxWidth: 640,
    alignSelf: "stretch",
    alignItems: "center",
    justifyContent: "center",
    // gap: 24,
    // paddingHorizontal: 16,
  },
  featuresRightColumn: {
    flex: 1,
    width: 800,
    alignItems: "flex-start",
    justifyContent: "center",
    paddingHorizontal: 20,
  },

  leftColumn: {
    flex: 1,
    justifyContent: "center",
    maxWidth: 500,
  },
  rightColumn: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  featuresContainer: {
    width: "100%",
    gap: 12,
    marginBottom: 32,
  },
  featureButton: {
    flexDirection: "row",
    alignItems: "center",
    height: 52,
    borderRadius: 12,
    backgroundColor: Colors.whiteColor,
    paddingHorizontal: 20,
    gap: 12,
  },
  featureButtonSelected: {
    backgroundColor: "rgba(63, 123, 141, 0.1)",
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: Colors.grayColor,
    justifyContent: "center",
    alignItems: "center",
  },
  checkmark: {
    fontSize: 14,
    color: Colors.bubbleColor,
    fontWeight: "bold",
  },
  featureText: {
    fontSize: 14,
    color: Colors.blackColor,
  },
  previewBox: {
    width: 400,
    height: 300,
    borderRadius: 16,
    backgroundColor: Colors.lightGrayColor,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: Colors.borderColor,
  },
  previewText: {
    fontSize: 18,
    color: Colors.grayColor,
  },

  logoPlaceholder: {
    fontSize: 24,
    color: Colors.bubbleColor,
    fontWeight: "600",
  },
  logoUploadContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 24,
    marginBottom: 24,
    padding: 24,
    borderRadius: 16,
    border: "1px dashed gray",
    borderColor: Colors.borderColor,
    transition: "all 0.2s ease-in-out",
    cursor: "pointer",
    width: "100%",
    maxWidth: 360,
    alignSelf: "center",
  },
  logoUploadContainerDragOver: {
    borderColor: Colors.bubbleColor,
    backgroundColor: `${Colors.bubbleColor}12`,
  },
  logoCircleContainer: {
    position: "relative",
  },
  logoCircle: {
    backgroundColor: Colors.lightGrayColor,
    justifyContent: "center",
    alignItems: "center",
  },
  deleteButton: {
    position: "absolute",
    top: -8,
    right: -8,
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 3,
    borderColor: Colors.grayColor,
    borderWidth: 1,
    backgroundColor: "rgba(240, 240, 240, 1)",
    transition: "all 0.2s ease-in-out",
    cursor: "pointer",
  },
  deleteButtonHovered: {
    backgroundColor: Colors.redColor,
    borderColor: Colors.redColor,
    shadowOpacity: 0.2,
    shadowRadius: 6,
    // transform: [{ scale: 1.1 }],
  },
  uploadButtonContainer: {
    alignItems: "center",
    gap: 12,
  },
  uploadButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    transition: "all 0.2s ease-in-out",
    cursor: "pointer",
    // backgroundColor: Colors.whiteColor,
  },
  uploadButtonHovered: {
    backgroundColor: `${Colors.bubbleColor}15`,
    borderColor: Colors.bubbleColor,
  },
  uploadButtonText: {
    fontSize: 14,
    color: Colors.bubbleColor,
  },
  dragDropText: {
    fontSize: 12,
    color: Colors.grayColor,
    textAlign: "center",
    fontStyle: "italic",
    maxWidth: 130,
  },
  uploadButtonDisabled: {
    opacity: 0.6,
    borderColor: Colors.grayColor,
  },
  uploadButtonTextDisabled: {
    color: Colors.grayColor,
  },
  errorContainer: {
    width: "100%",
    maxWidth: 360,
    marginBottom: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#fee",
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: "#f56565",
  },
  errorText: {
    fontSize: 14,
    color: "#c53030",
    textAlign: "center",
  },
  skipButton: {
    marginTop: 16,
  },
  skipText: {
    fontSize: 14,
    color: Colors.grayColor,
    textAlign: "center",
  },
  skipTextHovered: {
    color: "rgba(138, 138, 138, 1)",
  },
  preferencesContainer: {
    width: "100%",
    maxWidth: 600,
    gap: 12,
    marginBottom: 24,
  },
  preferenceRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    // height: 52,
    paddingHorizontal: 16,
    backgroundColor: Colors.whiteColor,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.borderColor,
  },
  preferenceLabelContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginRight: 16,
  },
  preferenceLabel: {
    fontSize: 14,
    color: Colors.blackColor,
  },
  tooltipIconContainer: {
    cursor: "pointer",
    opacity: 0.7,
    transition: "opacity 0.2s ease-in-out",
    // marginLeft: 12,
  },
  preferenceOptions: {
    flexDirection: "row",
    // gap: 8,
  },
  preferenceOption: {
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: 10,
    // backgroundColor: Colors.lightGrayColor,
    borderWidth: 1,
    borderColor: "transparent",
    width: 80,
    alignItems: "center",
    justifyContent: "center",
  },
  preferenceOptionMobile: {
    paddingHorizontal: 10,
    width: "auto",
    minWidth: 62,
  },
  preferenceOptionSelected: {
    // backgroundColor: Colors.whiteColor,
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    backgroundColor: `${Colors.bubbleColor}10`,
    transitionProperty: "background-color",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease-in-out",
  },
  preferenceOptionText: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.blackColor,
    textAlign: "center",
  },
  preferenceOptionTextSelected: {
    color: Colors.blackColor,
  },
  athletesOptionsContainer: {
    width: "100%",
    maxWidth: 360,
    gap: 12,
    marginTop: 40,
  },
  athleteOptionButton: {
    width: "100%",
    height: 46,
    borderRadius: 14,
    backgroundColor: Colors.whiteColor,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: Colors.borderColor,
    transitionProperty: "background-color",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease-in-out",
  },
  athleteOptionButtonHovered: {
    borderColor: Colors.grayColor,
  },
  athleteTypeOptionsContainer: {
    maxWidth: 360,
    width: "100%",
    gap: 12,
    marginTop: 0,
    marginBottom: 24,
  },
  athleteTypeOptionButton: {
    width: "100%",
    height: 46,
    borderRadius: 14,
    backgroundColor: Colors.whiteColor,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    transitionProperty: "background-color",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease-in-out",
  },
  athleteTypeCheckbox: {
    width: 16,
    height: 16,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    justifyContent: "center",
    alignItems: "center",
  },
  athleteTypeCheckboxSelected: {
    backgroundColor: Colors.bubbleColor,
    borderColor: Colors.bubbleColor,
  },
  athleteTypeCheckmark: {
    fontSize: 12,
    color: Colors.whiteColor,
    fontWeight: "bold",
  },
  athleteTypeText: {
    fontSize: 14,
    fontWeight: "500",
    color: Colors.blackColor,
    marginLeft: 12,
    flex: 1,
  },
  cardGridContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 24,
    justifyContent: "center",
    maxWidth: 640,
    width: "100%",
  },
});
